import React, { useEffect, useState } from "react";
import {
  Button,
  Flex,
  Heading,
  SimpleGrid,
  Spacer,
  VStack
} from "@chakra-ui/react";
import { AiOutlinePlus } from "react-icons/ai";

import {
  createUser,
  fetchAdminUsers,
  updateUser,
  deleteUser
} from "api/webservice/users";
import { AuthenticatedLayout } from "layouts";
import { User } from "types/user";
import AdminUsersDataTable from "./AdminUsersDataTable";
import AdminUserForm from "./AdminUserForm";
import { useToast } from "hooks";
import { useModal } from "context/modal";

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const { openModal, closeModal, setTitle, setContent } = useModal()!;
  const toast = useToast();

  useEffect(() => {
    handleFetchUsers();
  }, []);

  const refreshUsersTable = (): void => {
    closeModal();
    handleFetchUsers();
  };

  const handleFetchUsers = (): void => {
    fetchAdminUsers().then((response: User[]) => {
      if (response) setUsers(response);
    });
  };

  const handleCreateUser = (user: User, copy_id: string): void => {
    createUser({ ...user, copy_id })
      .then(() => {
        refreshUsersTable();
      })
      .catch(error => {
        toast({
          title: "Error creating user",
          description: error.response.data.response.errors[0].message,
          status: "error"
        });
      });
  };

  const handleUpdateUser = (user: User): void => {
    updateUser({ ...user })
      .then(() => {
        refreshUsersTable();
      })
      .catch(error => {
        toast({
          title: "Error updating user",
          description: error.response.data.response.errors[0].message,
          status: "error"
        });
      });
  };

  const handleDeleteUser = (user: User): void => {
    if (
      !window.confirm(
        `Are you sure you want to delete ${user.first_name} ${user.last_name}? \n (this action can be undone)`
      )
    ) {
      return;
    }

    closeModal();
    deleteUser({ ...user })
      .then(() => {
        refreshUsersTable();
        toast({
          title: "Successfully deleted user",
          description: "",
          status: "success"
        });
      })
      .catch(error => {
        toast({
          title: "Error deleting user",
          description: error.response.data.response.errors[0].message,
          status: "error"
        });
      });
  };

  const openCreateModal = () => {
    setTitle("Create New Coach");
    setContent(
      <AdminUserForm
        saveUser={handleCreateUser}
        deleteUser={handleDeleteUser}
      />
    );
    openModal();
  };

  useEffect(() => {
    handleFetchUsers();
  }, [setUsers]);

  return (
    <AuthenticatedLayout>
      <VStack spacing={8} alignItems="flex-start">
        <SimpleGrid columns={3} w="100%">
          <Heading size="lg">Coach Management</Heading>
          <Spacer />
          <Flex alignItems="center" justifyContent="flex-end">
            <Button
              size="md"
              variant="blue"
              rounded="full"
              iconSpacing={0}
              leftIcon={<AiOutlinePlus />}
              onClick={openCreateModal}
            >
              Add Coach
            </Button>
          </Flex>
        </SimpleGrid>
        <AdminUsersDataTable
          users={users}
          saveUser={handleUpdateUser}
          deleteUser={handleDeleteUser}
        />
      </VStack>
    </AuthenticatedLayout>
  );
};

export default Users;
